import {
  getElecBasePeakYearlyDifferenceChart,
  getElecBasePeakYearlyDifferenceComparisonTable,
  getElecBasePeakYearlyDifferenceDailyIndicators,
} from "$/api/market-monitoring/electricity-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import LineGraph from "$/lib/components/graphs/LineGraph";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import { CheckboxSet } from "$/lib/components/molecules/ChecboxSet";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import TableGraphWidget from "$/lib/components/widgets/TableGraphWidget";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { BasePeakYearlyDifferenceTableType } from "$/lib/types/api/market-monitoring/electricity-analysis.types";
import type { CellType, ColumnType } from "$/lib/types/Graphs.types";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import type { FutureProductPeriod } from "$/lib/types/models/FutureProduct.types";
import {
  format,
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import {
  BASE_ELEC_COLORS,
  handleOnChangeDefaultGraphsTabs,
  PEAK_ELEC_COLORS,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import {
  cn,
  getArrayKeys,
  getMinMax,
} from "$/lib/utils/functions/misc.functions";
import { numberFormatter } from "$/lib/utils/functions/util.functions";
import { useQuery } from "@tanstack/react-query";
import type { CellContext } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";

const BASE_COLORS = [...BASE_ELEC_COLORS];

const PEAK_COLORS = [...PEAK_ELEC_COLORS];

const COLORS = [...BASE_COLORS, ...PEAK_COLORS];

const LocalRenderTableNumberCell = <T extends object>({
  theme,
  className,
  ...cell
}: CellContext<T, number | string> & {
  theme: "blue" | "orange" | "red";
  className?: string;
}) => {
  const isNotANumber = !cell.getValue() && typeof cell !== "number";

  return (
    <span
      className={cn(
        `text-xs font-medium`,
        {
          "text-[#6177a9]": theme === "blue",
          "text-warning": theme === "orange",
          "text-danger": theme === "red",
          "text-black": isNotANumber,
        },
        className,
      )}
    >
      {isNotANumber ? "-" : numberFormatter(cell.getValue())}
    </span>
  );
};

type Props = {
  selectedDate: Date;
} & CustomDashboardProps;

const ElecBetweenCalendarsBasePeakDifference = ({
  selectedDate,
  id,
  isDraggable = false,
  isShowcase = false,
}: Props) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [filters, setFilters] = useState<{
    base: boolean;
    peak: boolean;
  }>({
    base: true,
    peak: false,
  });

  const [tablePeriod, setTablePeriod] = useState<FutureProductPeriod | "DAY">(
    "QUARTER",
  );

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const { data, isLoading } = useQuery({
    queryKey: ["get-elec-base-peak-yearly-difference-chart", dateRange],
    queryFn: () =>
      getElecBasePeakYearlyDifferenceChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const { data: tableData } = useQuery({
    queryKey: [
      "get-elec-analysis-future--yearly-difference-comparison-table",
      dateRange,
      tablePeriod,
    ],
    queryFn: () =>
      getElecBasePeakYearlyDifferenceComparisonTable({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
        period: tablePeriod,
      }),
  });

  const { data: daily } = useQuery({
    queryKey: ["get-elec-base-peak-yearly-difference-daily", selectedDate],
    queryFn: () =>
      getElecBasePeakYearlyDifferenceDailyIndicators({
        date: getDate(selectedDate),
      }),
  });

  const toDisplay = useMemo(
    () =>
      data
        ? filters.base && filters.peak
          ? data.mergedData
          : filters.base
            ? data.baseData
            : filters.peak
              ? data.peakData
              : []
        : [],
    [data, filters],
  );

  const keys = useMemo(() => getArrayKeys(toDisplay), [toDisplay]);

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const colors: Record<string, string> = useMemo(
    () =>
      keys.reduce(
        (acc, key, i) => ({
          ...acc,
          [key]:
            filters.base && filters.peak
              ? COLORS[i]
              : filters.base
                ? BASE_COLORS[i]
                : filters.peak
                  ? PEAK_COLORS[i]
                  : COLORS[i],
        }),
        {},
      ),
    [keys, filters],
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  const baseTableKeys = ["baseCal1_2", "baseCal1_3", "baseCal2_3"];
  const peakTableKeys = ["peakCal1_2", "peakCal1_3", "peakCal2_3"];

  const tableBounds = useMemo(() => {
    if (tableData) {
      const keysToCompare =
        filters.base && filters.peak
          ? [...baseTableKeys, ...peakTableKeys]
          : filters.base
            ? baseTableKeys
            : peakTableKeys;

      return getMinMax(tableData[tablePeriod], keysToCompare);
    }

    return { min: { value: 0 }, max: { value: 0 } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, tablePeriod, filters]);

  const columns: ColumnType<BasePeakYearlyDifferenceTableType>[] =
    useMemo(() => {
      const firstHeaders: ColumnType<BasePeakYearlyDifferenceTableType>[] = [
        {
          header: () => {},
          accessorKey: "year",
          cell: (cell) => {
            const values = cell.row.original;

            const display =
              tablePeriod === "QUARTER" || tablePeriod === "MONTH"
                ? [values.quarter, values.month]
                : [values.month, values.day];

            return (
              <Flexbox className="gap-0.5 whitespace-nowrap">
                <span className="text-xs font-medium text-black">
                  {cell.getValue()}
                </span>

                <span className="text-[10px] text-tertiary-600">
                  {display
                    .filter((v) => v)
                    .join(tablePeriod === "DAY" ? " " : " - ")}
                </span>
              </Flexbox>
            );
          },
        },
      ];

      return [
        ...firstHeaders,
        ...(filters.base
          ? [
              {
                header: "Base - Ecart Cal +1/+2",
                accessorKey: "baseCal1_2",
                cell: (cell: CellType<BasePeakYearlyDifferenceTableType>) => (
                  <LocalRenderTableNumberCell theme="blue" {...cell} />
                ),
              },
              {
                header: "Base - Ecart Cal +1/+3",
                accessorKey: "baseCal1_3",
                cell: (cell: CellType<BasePeakYearlyDifferenceTableType>) => (
                  <LocalRenderTableNumberCell theme="orange" {...cell} />
                ),
              },
              {
                header: "Base - Ecart Cal +2/+3",
                accessorKey: "baseCal2_3",
                cell: (cell: CellType<BasePeakYearlyDifferenceTableType>) => (
                  <LocalRenderTableNumberCell theme="red" {...cell} />
                ),
              },
            ]
          : []),

        ...(filters.peak
          ? [
              {
                header: "Peak - Ecart Cal +1/+2",
                accessorKey: "peakCal1_2",
                cell: (cell: CellType<BasePeakYearlyDifferenceTableType>) => (
                  <LocalRenderTableNumberCell
                    theme="blue"
                    className={cn(
                      filters.base && filters.peak && "text-[#64a34b]",
                    )}
                    {...cell}
                  />
                ),
              },
              {
                header: "Peak - Ecart Cal +1/+3",
                accessorKey: "peakCal1_3",
                cell: (cell: CellType<BasePeakYearlyDifferenceTableType>) => (
                  <LocalRenderTableNumberCell
                    theme="orange"
                    className={cn(
                      filters.base && filters.peak && "text-[#273c7b]",
                    )}
                    {...cell}
                  />
                ),
              },
              {
                header: "Peak - Ecart Cal +2/+3",
                accessorKey: "peakCal2_3",
                cell: (cell: CellType<BasePeakYearlyDifferenceTableType>) => (
                  <LocalRenderTableNumberCell
                    theme="red"
                    className={cn(
                      filters.base && filters.peak && "text-[#6480c2]",
                    )}
                    {...cell}
                  />
                ),
              },
            ]
          : []),
      ];
    }, [filters, tablePeriod]);

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key, i) => ({
        name: key,
        isVisible: true,
        color:
          filters.base && filters.peak
            ? COLORS[i]
            : filters.base
              ? BASE_COLORS[i]
              : filters.peak
                ? PEAK_COLORS[i]
                : COLORS[i],
      })),
    );
  }, [keys, filters]);

  return (
    <DisplayBox
      title="Ecart par période entre les Cal+1, Cal+2 et Cal+3 Base et Peak"
      Icon={LineChartIcon}
      theme="blue"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer midHeight>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight className="gap-2 rounded-3xl">
            <LineGraph
              data={toDisplay}
              graphVisibility={graphVisibility}
              title="Prix Stlt EEX"
              xAxisDataKey="date"
              colors={colors}
              theme="blue"
              keys={keys}
              leftMargin={-10}
              yAxisTicksNumber={10}
              height={530}
            />
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
          />

          <CheckboxSet<"base" | "peak">
            id="base-peak-calendar-difference-chart"
            title="Base / Peak"
            selected={filters}
            onChange={(id, value) =>
              setFilters((prev) => {
                const newValue = { ...prev, [id]: value };
                if (!newValue.base && !newValue.peak) return prev;
                return newValue;
              })
            }
            options={[
              { id: "base", label: "Base" },
              { id: "peak", label: "Peak" },
            ]}
            row
            className="items-center"
          />

          <Flexbox fullHeight className="gap-4 overflow-y-auto pb-1">
            <GraphFilter
              title="Légende"
              data={graphVisibility}
              handleOnChange={handleOnChangeGraphVisiblity}
            />

            {daily && !!daily.baseData.length && !!daily.peakData.length && (
              <Flexbox fullWidth className="rounded-lg shadow-displayBoxShadow">
                <Flexbox
                  fullWidth
                  className="border-b-[1px] border-primary-300 bg-primary-200 p-1 text-sm font-medium text-black"
                >
                  <p className="w-full text-center text-sm">
                    Ecarts produits CAL actuels
                  </p>
                  <p className="w-full text-center text-sm text-primary">
                    {format(selectedDate, "dd/MM/yy")}
                  </p>
                </Flexbox>

                <Flexbox fullWidth>
                  <Flexbox
                    row
                    fullWidth
                    className="daily-prods-grid border-b-[1px] border-primary-300"
                  >
                    <Flexbox
                      fullWidth
                      fullHeight
                      align="center"
                      justify="center"
                      className="bg-primary-200 p-1 text-sm font-medium text-black"
                    >
                      <p>Base</p>
                    </Flexbox>

                    <Flexbox className="m-auto px-1 py-1">
                      {daily.baseData.map((item) => (
                        <Flexbox
                          key={item.title}
                          row
                          fullWidth
                          align="center"
                          justify="between"
                          className="mb-1 gap-3 text-xs"
                        >
                          <p>{item.title}</p>
                          <p>{item.value} €/MWh</p>
                        </Flexbox>
                      ))}
                    </Flexbox>
                  </Flexbox>

                  <Flexbox row fullWidth className="daily-prods-grid">
                    <Flexbox
                      fullWidth
                      fullHeight
                      align="center"
                      justify="center"
                      className="bg-primary-200 p-1 text-sm font-medium text-black"
                    >
                      <p>Peak</p>
                    </Flexbox>

                    <Flexbox className="m-auto px-1 py-1">
                      {daily.peakData.map((item) => (
                        <Flexbox
                          key={item.title}
                          row
                          fullWidth
                          align="center"
                          justify="between"
                          className="mb-1 gap-3 text-xs"
                        >
                          <p>{item.title}</p>
                          <p>{item.value} €/MWh</p>
                        </Flexbox>
                      ))}
                    </Flexbox>
                  </Flexbox>
                </Flexbox>
              </Flexbox>
            )}

            <Flexbox fullWidth className="p-1">
              <TableGraphWidget<BasePeakYearlyDifferenceTableType>
                columns={columns}
                data={tableData ? tableData[tablePeriod] : []}
                tableBounds={tableBounds}
                hasLegend={false}
                selectedPeriod={tablePeriod}
                setSelectedPeriod={setTablePeriod}
              />
            </Flexbox>
          </Flexbox>
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default ElecBetweenCalendarsBasePeakDifference;
